import { Badge, Box, Group, NavLink, Paper, Title, Burger, Drawer, Stack } from '@mantine/core';
import {
  IconShield, IconUsers, IconKey, IconUserShield, IconDashboard,
  IconListTree, IconChartBar, IconBuildingStore, IconCategory, IconBox,
  IconShoppingCart, IconHierarchy, IconCloudDownload, IconShieldCheck, IconSettings,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { ReactNode } from 'react';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@teletienda/shared-auth-ui';
import { catalogAdminApi } from '@/lib/api/catalogAdmin';

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { t } = useTranslation('admin');
  const { t: tCatalog } = useTranslation('catalog');
  const { user } = useAuth();

  const isAdmin =
    user?.roles?.some(
      (role: string) => role === 'admin' || role === 'super_admin' || role === 'platform_admin'
    ) || false;

  const isCatalogSection = location.pathname.startsWith('/admin/catalog');

  const isActive = (path: string) => location.pathname === path;

  const { data: pendingModerationCount } = useQuery({
    queryKey: ['catalog', 'moderation', 'pending-count'],
    queryFn: async () => {
      const res = await catalogAdminApi.listModerationRequests({ status: 'pending', limit: 1 });
      return (res as { total?: number }).total ?? 0;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    enabled: isAdmin,
  });

  const navigationItems = [
    { path: '/admin', label: t('dashboard'), icon: IconDashboard },
    { path: '/admin/users', label: t('users'), icon: IconUsers },
    { path: '/admin/merchants', label: t('merchants'), icon: IconBuildingStore },
    { path: '/admin/roles', label: t('roles'), icon: IconUserShield },
    { path: '/admin/permissions', label: t('permissions'), icon: IconKey },
    { path: '/admin/news', label: t('news.nav'), icon: IconSpeakerphone },
    { path: '/admin/settings', label: t('settings'), icon: IconSettings },
  ];

  const catalogNavItems = [
    { path: '/admin/catalog', label: tCatalog('nav.dashboard'), icon: IconChartBar },
    { path: '/admin/catalog/brands', label: tCatalog('nav.brands'), icon: IconBuildingStore },
    { path: '/admin/catalog/categories', label: tCatalog('nav.categories'), icon: IconCategory },
    { path: '/admin/catalog/models', label: tCatalog('nav.models'), icon: IconBox },
    { path: '/admin/catalog/products', label: tCatalog('nav.products'), icon: IconShoppingCart },
    { path: '/admin/catalog/gpc', label: tCatalog('nav.gpc'), icon: IconHierarchy },
    { path: '/admin/catalog/icecat', label: tCatalog('nav.icecat'), icon: IconCloudDownload },
    { path: '/admin/catalog/moderation', label: tCatalog('nav.moderation'), icon: IconShieldCheck, badge: pendingModerationCount },
    { path: '/admin/catalog/settings', label: tCatalog('nav.settings'), icon: IconSettings },
  ];

  const NavigationContent = () => {
    const items = isCatalogSection ? catalogNavItems : navigationItems;
    const headerIcon = isCatalogSection ? <IconListTree size={24} /> : <IconShield size={24} />;
    const headerTitle = isCatalogSection ? tCatalog('nav.catalog') : t('admin_panel');

    return (
      <Stack gap="xs">
        <Group mb="md" align="center" gap="xs">
          {headerIcon}
          <Title order={4}>{headerTitle}</Title>
        </Group>
        {items.map((item) => (
          <NavLink
            key={item.path}
            component={Link}
            to={item.path}
            label={item.label}
            leftSection={<item.icon size={20} />}
            active={isActive(item.path)}
            rightSection={
              'badge' in item && (item as { badge?: number }).badge && (item as { badge?: number }).badge! > 0 ? (
                <Badge size="xs" variant="filled" color="red" circle>
                  {(item as { badge?: number }).badge}
                </Badge>
              ) : undefined
            }
            variant="subtle"
            onClick={isMobile ? closeMobileNav : undefined}
          />
        ))}
      </Stack>
    );
  };

  // Mobile layout with drawer
  if (isMobile) {
    return (
      <>
        <Group justify="space-between" p="md" bg="gray.0" mb="md">
          <Group gap="xs">
            {isCatalogSection ? <IconListTree size={20} /> : <IconShield size={20} />}
            <Title order={5}>{isCatalogSection ? tCatalog('nav.catalog') : t('admin_panel')}</Title>
          </Group>
          <Burger opened={mobileNavOpened} onClick={toggleMobileNav} size="sm" />
        </Group>

        <Drawer
          opened={mobileNavOpened}
          onClose={closeMobileNav}
          title={
            <Group gap="xs">
              {isCatalogSection ? <IconListTree size={20} /> : <IconShield size={20} />}
              <Title order={5}>{t('navigation')}</Title>
            </Group>
          }
          padding="md"
          size="sm"
        >
          <NavigationContent />
        </Drawer>

        <Box px="md" pb="md">
          {children}
        </Box>
      </>
    );
  }

  // Desktop layout with sidebar
  return (
    <Group align="flex-start" gap="md" wrap="nowrap">
      <Paper shadow="sm" p="md" withBorder style={{ minWidth: 250 }}>
        <NavigationContent />
      </Paper>
      <Box style={{ flex: 1 }}>{children}</Box>
    </Group>
  );
}
